import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Radio, Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const links = [
  { to: "/", label: "Home", target: "" },
  { to: "/meshcore", label: "MeshCore", target: "" },
  { to: "/devices", label: "Devices", newtargetwin: "" },
  { to: "/settings", label: "Settings", target: "" },
  { to: "/mqtt", label: "MQTT", target: "" },
  { to: "/blog", label: "Blog", target: "" },
  { to: "https://us-east.corecomms.net", label: 'Live Map', target: "_blank" }
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  return (
    <header
      className="sticky top-0 z-50 bg-[#0A0C10]/85 backdrop-blur-md border-b border-[#272E3A]"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
            <div className="relative">
              <div className="w-9 h-9 bg-[#FF5500] flex items-center justify-center">
                <Radio className="w-5 h-5 text-black" strokeWidth={2.5} />
              </div>
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#10B981] rounded-full animate-pulse-dot" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-black text-white text-base tracking-tight">Eastern US</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">Meshcore</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                target={l.target}
                data-testid={`nav-${l.label.toLowerCase().replace(' ', '-')}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium transition-colors border-b-2 ${isActive
                    ? "text-[#FF5500] border-[#FF5500]"
                    : "text-slate-300 border-transparent hover:text-white hover:border-[#272E3A]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user && user.role === "admin" ? (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`ml-2 px-3 py-2 text-sm font-mono uppercase tracking-wider border ${location.pathname.startsWith("/admin")
                  ? "bg-[#FF5500] text-black border-[#FF5500]"
                  : "border-[#272E3A] text-[#FF5500] hover:bg-[#1C2028]"
                  }`}
              >
                Admin
              </Link>
            ) : null}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://discord.gg/3TPXsHBv6t"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-discord-link"
              className="text-sm font-mono uppercase tracking-wider text-[#94A3B8] hover:text-white transition-colors"
            >
              [Discord]
            </a>
            <Link
              to="/connect"
              data-testid="navbar-cta"
              className="px-4 py-2 bg-[#FF5500] hover:bg-[#FF7326] text-white text-sm font-semibold tracking-wide transition-colors"
            >
              Get Connected →
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen(!open)}
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#272E3A] bg-[#0A0C10]" data-testid="mobile-menu">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-sm font-medium ${isActive ? "bg-[#1C2028] text-[#FF5500]" : "text-slate-300 hover:bg-[#13161C]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user && user.role === "admin" && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="px-3 py-2.5 text-sm font-mono uppercase text-[#FF5500]"
              >
                Admin Dashboard
              </Link>
            )}
            <a
              href="https://discord.gg/3TPXsHBv6t"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2.5 text-sm text-[#94A3B8]"
            >
              Discord →
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
